import { useDairyAnimalHistory } from '@/api/hooks';
import { Card, CardBody } from '@/components/ui/Card';
import { formatLiters, milkingShiftLabel } from '@/lib/dairy';
import { formatDate } from '@/lib/format';
import { Link } from 'react-router-dom';

export function AnimalDairyPanel({ animalId }: { animalId: number }) {
  const { data, isLoading, isError } = useDairyAnimalHistory(animalId);
  const lactation = data?.lactation;

  if (isLoading) {
    return <div className="h-40 rounded-2xl bg-manejai-sand/40 animate-pulse" />;
  }

  if (isError) {
    return (
      <Card>
        <CardBody className="text-sm text-red-600">Não foi possível carregar o histórico leiteiro.</CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="rounded-xl bg-manejai-cream/60 px-4 py-3">
          <p className="text-xs text-manejai-bark/55">Lactação atual</p>
          <p className="font-semibold text-manejai-forest">{lactation?.lactation_number ?? '—'}</p>
        </div>
        <div className="rounded-xl bg-manejai-cream/60 px-4 py-3">
          <p className="text-xs text-manejai-bark/55">DEL</p>
          <p className="font-semibold text-manejai-forest">
            {lactation?.del != null ? `${lactation.del} dias` : '—'}
          </p>
        </div>
        <Link
          to="/controle-leiteiro/lancamentos"
          className="ml-auto self-center text-sm text-manejai-moss hover:underline"
        >
          Ir ao Controle Leiteiro
        </Link>
      </div>

      {(data?.entries.length ?? 0) === 0 ? (
        <Card>
          <CardBody className="py-10 text-center text-sm text-manejai-bark/55">
            Sem lançamentos de leite para este animal.
          </CardBody>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/55">
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Turno</th>
                  <th className="px-4 py-3 font-medium">Produção</th>
                  <th className="px-4 py-3 font-medium">Produção diária</th>
                  <th className="px-4 py-3 font-medium">Lactação</th>
                  <th className="px-4 py-3 font-medium">DEL</th>
                  <th className="px-4 py-3 font-medium">Observações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-manejai-sand/50">
                {data!.entries.map((row) => (
                  <tr key={row.event_id}>
                    <td className="px-4 py-3">{formatDate(row.event_date)}</td>
                    <td className="px-4 py-3">{milkingShiftLabel(row.milking_shift)}</td>
                    <td className="px-4 py-3 font-medium">{formatLiters(row.production_liters)}</td>
                    <td className="px-4 py-3">{formatLiters(row.daily_liters)}</td>
                    <td className="px-4 py-3">{row.lactation_number ?? '—'}</td>
                    <td className="px-4 py-3">{row.del ?? '—'}</td>
                    <td className="px-4 py-3 text-manejai-bark/70 max-w-[14rem] truncate">
                      {row.notes || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
